import Container, { Service } from 'typedi';
import SettingsApi from '../../../APIs/SettingsApi';
import { LocalSimpleStoreService } from '../storage/implementations/simple-store';
import { ViewInjector } from '../view-injector';
import { ILogger } from '@sparkware/uc-sdk-core';
import { LoggerProvider } from '../logger';
import { UserContextToken, WindowToken } from '../../injection-tokens';
import { IUserContext } from '../user-context/user-context-interface';

@Service()
export class RealityCheck {
    private _lastLoginDate: Date | null = null;
    private _realityCheckMessage: string = '';
    private static readonly MS_PER_SECOND = 1000;
    private static readonly SECONDS_PER_MINUTE = 60;

    private static readonly MINUTES_PER_HOUR = 60;
    private static readonly HOUR_TIMEOUT =
        RealityCheck.MS_PER_SECOND *
        RealityCheck.SECONDS_PER_MINUTE *
        RealityCheck.MINUTES_PER_HOUR;
    protected readonly _logger: ILogger;
    private readonly _localSimpleStoreService: LocalSimpleStoreService;
    private readonly _viewInjector: ViewInjector;
    private readonly _window: Window;
    private readonly _userContext: IUserContext;

    constructor() {
        this._logger = Container.get(LoggerProvider).getLogger('RealityCheck');
        this._localSimpleStoreService = Container.get(LocalSimpleStoreService);
        this._viewInjector = Container.get(ViewInjector);
        this._window = Container.get(WindowToken);
        this._userContext = Container.get(UserContextToken);
    }

    public initRealityCheck(): void {
        if (this._userContext.IsAuthenticated) {
            void this.realityCheck();
        }
    }

    public async realityCheck(): Promise<void> {
        const getRealityCheckResponse = await SettingsApi.GetRealityCheck();
        if (getRealityCheckResponse.response) {
            this.InsertRealityCheckDiv();
            const currentDate = new Date();
            if (!this._localSimpleStoreService.get('realityCheckTimeout')) {
                this._localSimpleStoreService.set('realityCheckTimeout', currentDate.toISOString());
            }

            const lastLoginDateStr = this._localSimpleStoreService.get('realityCheckTimeout');
            this._lastLoginDate = lastLoginDateStr ? new Date(lastLoginDateStr) : currentDate;
            const dateDifference = currentDate.getTime() - this._lastLoginDate.getTime();

            const hours = Math.floor(dateDifference / RealityCheck.HOUR_TIMEOUT);
            const secondsDifference = dateDifference - hours * RealityCheck.HOUR_TIMEOUT;
            const initialDelay = RealityCheck.HOUR_TIMEOUT - secondsDifference;
            this._realityCheckMessage =
                '##preposition## trecut ##hours## de când te-ai conectat la 888sport.ro';

            setTimeout(() => this.showRealityCheck(), initialDelay);
        }
    }

    private showRealityCheck(): void {
        if (!this._lastLoginDate) {
            return;
        }

        if (this._localSimpleStoreService.get('authorizationData')) {
            setTimeout(() => this.showRealityCheck(), RealityCheck.HOUR_TIMEOUT);
            const now = new Date();
            const hoursPassed =
                (now.getTime() - this._lastLoginDate.getTime()) / RealityCheck.HOUR_TIMEOUT;
            const roundedHours = Math.round(hoursPassed);
            const hoursText = roundedHours === 1 ? `${roundedHours} oră` : `${roundedHours} ore`;
            const prepositionText = roundedHours === 1 ? 'A ' : 'Au ';

            const realityCheckElem = this._window.document.getElementById('realityCheck');
            if (realityCheckElem) {
                const pElem = realityCheckElem.querySelector('p');
                if (pElem) {
                    pElem.textContent = this._realityCheckMessage
                        .replace('##hours##', hoursText)
                        .replace('##preposition##', prepositionText);
                }
            }
            this._viewInjector.InjectAndShowRealityCheckWrappingDiv(
                'realityCheck',
                200,
                700,
                ((text: string) => {
                    const deviceData = this._window.pageContextManager.getDeviceData();
                    const realityCheckCopy = deviceData.isMobile
                        ? 'realityCheckcopyMobile'
                        : 'realityCheckcopy';
                    return text.replace('realityCheck', realityCheckCopy);
                }).bind(this),
                'realityCheckPopup',
            );

            setTimeout(() => {
                this._viewInjector.CloseRealityCheckWrapDiv();
            }, 5 * RealityCheck.MS_PER_SECOND);
        }
    }

    private InsertRealityCheckDiv(): void {
        if (!this._window.document.getElementById('realityCheck')) {
            const realityCheckDiv = this._window.document.createElement('div');
            realityCheckDiv.id = 'realityCheck';
            const realityCheckParagraph = this._window.document.createElement('p');
            realityCheckDiv.appendChild(realityCheckParagraph);
            realityCheckDiv.style.display = 'none';
            this._window.document.body.appendChild(realityCheckDiv);
        }
    }
}
